import React, { useCallback, useMemo, ReactElement } from 'react';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Checkbox from '../../../../components/checkbox/checkbox.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import LongClickTableRow from '../../../../components/long-click-table-row/long-click-table-row.component';
import TableCellCheckbox from '../../../../components/table-cell-checkbox/table-cell-checkbox.component';
import IconTextBlock from '../../../../components/icon-text-block/icon-text-block.component';
import DealFileRowForm from '../deal-file-row-form/deal-file-row-form.component';
import EditIcon from '../../../../icons/edit-icon';
import MoreIcon from '../../../../icons/more-icon';
import DownloadIcon from '../../../../icons/download-icon';
import FormatService from '../../../../services/format-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { DEAL_FILE_EDIT } from '../../../../constants/entity/permissions';
import { DATA_DEAL_FILE_ID, DATA_IS_ANALYZABLE } from '../../../../constants/system/data-attributes';
import { MIME_TYPE_APPLICATION_PDF } from '../../../../constants/system/mime-types';

const DealFileRow: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ID_PREFIX_CHECKBOX_EXPORT: string = 'checkbox-export';

  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isSelected,
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isSimple,
    dealFileItem,
    dealFileIndex,
    dealFileGroup,
    dealFileFile,
    dealFileActiveIds,
    fileDropDeps,
    inputRefs,
    actionMenuItems,
    accessLevelList,
    onRowClick,
    onLongClick,
    onInlineSave,
    onInlineCancel,
    onInlineEditModeButtonClick,
    onOpenModal,
    onFileChange,
    onFileDelete,
    onFileViewClick,
    onOpenActionMenu,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const dealFileEditPermissions: string[] = useMemo((): string[] => [DEAL_FILE_EDIT], []);

  const actionMenuPermissions: string[] = useMemo((): string[] => (
    actionMenuItems && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | null => (
      actionMenuItem.permission || null
    )).filter((permissionItem: string | null): boolean => !!permissionItem) as string[]
  ), [actionMenuItems]);

  const openModal: Function = useCallback((): void => {
    onOpenModal(dealFileItem, dealFileGroup);
  }, [dealFileItem, dealFileGroup]);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const checkIsFileAnalyzable: Function = useCallback((file?: FileItem): boolean => {
    return !!file && !!file.mimeType && file.mimeType === MIME_TYPE_APPLICATION_PDF;
  }, []);

  return (
    isEditMode && isActive ? (
      <DealFileRowForm
        isActive={isActive}
        isSaving={isSaving}
        isSaveDisabled={isSaveDisabled}
        isInlineSaving={isInlineSaving}
        isSimple={isSimple}
        dealFileIndex={dealFileIndex}
        dealFileData={dealFileItem}
        dealFileGroupData={dealFileGroup}
        dealFileFile={dealFileFile}
        inputRefs={inputRefs}
        fileDropDeps={fileDropDeps}
        accessLevelList={accessLevelList}
        onFileChange={onFileChange}
        onFileDelete={onFileDelete}
        onOpenModal={openModal}
        onInlineSave={onInlineSave}
        onInlineCancel={onInlineCancel}
      />
    ) : (
      dealFileItem.file && (
        <LongClickTableRow
          isButton
          isActive={isSelected}
          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
            onRowClick(event, dealFileItem, dealFileGroup);
          }}
          onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
            onLongClick(event, dealFileGroup.id, dealFileItem.id);
          }}
        >
          {
            (isExportMode || isSelectMode) && (
              <TableCellCheckbox isSticky isStickyLeft>
                <Checkbox
                  isChecked={isSelected}
                  withNoPadding={!isSmallScreen}
                  id={`${ID_PREFIX_CHECKBOX_EXPORT}-${dealFileItem.id}`}
                />
              </TableCellCheckbox>
            )
          }
          <TableCell isText width="30%">
            {dealFileItem.file.name}
          </TableCell>
          {
            isEditMode && (
              <TableCell />
            )
          }
          <TableCell>
            {
              dealFileItem.file && (
                <TextBlock>
                  {`Date: ${dealFileItem.file.date}`}
                </TextBlock>
              )
            }
          </TableCell>
          <TableCell width="20%">
            {
              dealFileItem.user && (
                <TextBlock>
                  {`By ${dealFileItem.user.fullName}`}
                </TextBlock>
              )
            }
          </TableCell>
          <TableCell isTextRight width="15%">
            <FlexContainer isCentered>
              {
                !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && dealFileItem.file && (
                  <FilteredLink
                    isExternal
                    link={dealFileItem.file.link}
                    onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onFileViewClick(event, dealFileItem.file, dealFileGroup.name);
                    }}
                  >
                    <IconTextBlock icon={<DownloadIcon />} />
                  </FilteredLink>
                )
              }
            </FlexContainer>
          </TableCell>
          <TableCell isActions isTextRight isSticky isStickyRight width="5%" onClick={onActionCellClick}>
            {
              !isExportMode && !isSelectMode && !isCustomizeMode
              && (!isEditMode || !dealFileActiveIds || !dealFileActiveIds.includes(dealFileItem.id)) && (
                <CheckAccess permissions={dealFileEditPermissions}>
                  <ActionButton
                    withSmallPadding
                    isHidden={!!isSaving}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onInlineEditModeButtonClick(event, dealFileItem.id);
                    }}
                  >
                    <EditIcon />
                  </ActionButton>
                </CheckAccess>
              )
            }
            {
              !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
                <CheckAccess permissions={actionMenuPermissions}>
                  <ActionButton
                    withPopup
                    withSmallPadding
                    isHidden={!!isSaving}
                    onClick={onOpenActionMenu}
                    {...{
                      [DATA_DEAL_FILE_ID]: dealFileItem.id,
                      [DATA_IS_ANALYZABLE]: checkIsFileAnalyzable(dealFileItem.file)
                        ? FormatService.BOOLEAN_VALUE_TRUE : null,
                    }}
                  >
                    <MoreIcon />
                  </ActionButton>
                </CheckAccess>
              )
            }
          </TableCell>
        </LongClickTableRow>
      )
    )
  );
});

export default DealFileRow;
